import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { SubscriptionBasedComponent } from 'src/app/subscription-helpers/subscription-based.component';
import { OVERRIDES, PriceLine } from 'src/app/model/price-line';
import Board from 'src/app/model/product/board/board';
import { PlacementInvoiceAutoFillPriceField } from 'src/app/model/product/board/sign-size';
import { BoardPartAddedEvent } from 'src/app/events/board-part-added';
import { Subject } from 'rxjs';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import { LoadingService } from 'src/app/services/loading.service';
import { SHORT_DEBOUNCE_TIME } from 'src/app/services/request.service';
import BoardPart from 'src/app/model/product/board/board-part';
import ProductHelper from 'src/app/helpers/product-helper';
import {
  ProductSizeChangedEvent,
  ProjectDisplayObservableService,
} from 'src/app/services/project-display-observable.service';
import {
  SubProject,
  SUB_PROJECT_TYPES,
} from 'src/app/model/project/sub-project';

@Component({
  selector: 'board-placement-price',
  templateUrl: './board-placement-price.component.html',
  styleUrls: ['./board-placement-price.component.scss'],
})
export class BoardPlacementPriceComponent
  extends SubscriptionBasedComponent
  implements OnInit
{
  @Input()
  inputList: PriceLine[];

  @Input()
  inputBoard: Board;

  @Input()
  subProject: SubProject;

  @Input()
  boardPartAdded: Subject<BoardPartAddedEvent>;

  @Input()
  companyFactor: number;

  @Input()
  isRetailer: boolean;

  @Output()
  output = new EventEmitter<PriceLine[]>();

  constructor(
    private loadingService: LoadingService,
    private projectDisplayObservableService: ProjectDisplayObservableService
  ) {
    super();
  }

  init = false;

  boardPartToProductionPrice = new Map<number, PriceLine>();
  boardPartToConstructionPrice = new Map<number, PriceLine>();
  boardPartToPlacementPrice = new Map<number, PriceLine>();
  boardPartToDistanceToBottomPrice = new Map<number, PriceLine>();
  boardPartToTransportPrice = new Map<number, PriceLine>();
  boardPartToPaintedPostsPrice = new Map<number, PriceLine>();
  boardPartToRepoPrice = new Map<number, PriceLine>();
  otherPrices: PriceLine[] = [];

  savePrices = new Subject();

  ngOnInit() {
    this.initPrices();
    this.addSubscription(
      this.savePrices,
      (outputList) => {
        this.combinePrices();
      },
      () => {},
      SHORT_DEBOUNCE_TIME
    );
    if (this.boardPartAdded != null) {
      this.addSubscription(
        this.boardPartAdded,
        (event: BoardPartAddedEvent) => {
          if (event.boardId === this.inputBoard.id) {
            this.initEmptyBoardPartPrice(event.boardPart);
            this.combinePrices();
          }
        }
      );
    }
    this.addSubscription(
      this.projectDisplayObservableService.getProductSizeChangedSubscribable(),
      (event: ProductSizeChangedEvent) => {
        if (
          event != null &&
          event.productId === this.inputBoard.id &&
          !this.subProject.quotationSent
        ) {
          this.fillPrices(event);
        }
      }
    );
    this.addSubscription(
      this.projectDisplayObservableService.getPaintedPostsChangedSubscribable(),
      () => this.combinePrices(),
      () => {},
      SHORT_DEBOUNCE_TIME
    );
    this.init = true;
  }

  initPrices() {
    if (!CollectionHelper.isPopulated(this.inputList)) {
      this.initPricesEmptyInput();
    } else {
      this.initPricesFromInput();
    }
  }

  initPricesEmptyInput() {
    this.initEmptyBoardPartPrices();
  }

  initPricesFromInput() {
    this.inputList.forEach((p) => {
      let addToOtherPrices = true;
      if (p.boardPart != null && p.boardPart.id != null) {
        if (
          p.override === OVERRIDES.PRODUCTION_PRICE.value &&
          this.boardPartToProductionPrice.get(p.boardPart.id) == null
        ) {
          this.boardPartToProductionPrice.set(p.boardPart.id, p);
          addToOtherPrices = false;
        }
        if (
          p.override === OVERRIDES.CONSTRUCTION_PRICE.value &&
          this.boardPartToConstructionPrice.get(p.boardPart.id) == null
        ) {
          this.boardPartToConstructionPrice.set(p.boardPart.id, p);
          addToOtherPrices = false;
        }
        if (
          p.override === OVERRIDES.PLACEMENT_PRICE.value &&
          this.boardPartToPlacementPrice.get(p.boardPart.id) == null
        ) {
          this.boardPartToPlacementPrice.set(p.boardPart.id, p);
          addToOtherPrices = false;
        }
        if (
          p.override === OVERRIDES.DISTANCE_TO_BOTTOM_PRICE.value &&
          this.boardPartToDistanceToBottomPrice.get(p.boardPart.id) == null
        ) {
          this.boardPartToDistanceToBottomPrice.set(p.boardPart.id, p);
          addToOtherPrices = false;
        }
        if (
          p.override === OVERRIDES.TRANSPORT_PRICE.value &&
          this.boardPartToTransportPrice.get(p.boardPart.id) == null
        ) {
          this.boardPartToTransportPrice.set(p.boardPart.id, p);
          addToOtherPrices = false;
        }
        if (
          p.override === OVERRIDES.PAINTED_POSTS_PRICE.value &&
          this.boardPartToPaintedPostsPrice.get(p.boardPart.id) == null
        ) {
          this.boardPartToPaintedPostsPrice.set(p.boardPart.id, p);
          addToOtherPrices = false;
        }
        if (
          p.override === OVERRIDES.REPO_PRICE.value &&
          this.boardPartToRepoPrice.get(p.boardPart.id) == null
        ) {
          this.boardPartToRepoPrice.set(p.boardPart.id, p);
          addToOtherPrices = false;
        }
      }
      if (addToOtherPrices) {
        this.otherPrices.push(p);
      }
    });
    this.initEmptyBoardPartPrices();
  }

  initEmptyBoardPartPrices() {
    if (CollectionHelper.isPopulated(this.inputBoard.boardParts)) {
      this.inputBoard.boardParts.forEach((part) => {
        this.initEmptyBoardPartPrice(part);
      });
    }
  }

  initEmptyBoardPartPrice(part: BoardPart) {
    if (part.id == null) {
      return;
    }
    if (this.boardPartToProductionPrice.get(part.id) == null) {
      let productionPrice = new PriceLine();
      productionPrice.product = this.inputBoard;
      productionPrice.boardPart = part;
      productionPrice.text = 'Aanmaakprijs';
      productionPrice.override = OVERRIDES.PRODUCTION_PRICE.value;
      this.fillBoardPartPrice(
        productionPrice,
        part,
        PlacementInvoiceAutoFillPriceField.PRODUCTION_PRICE,
        PlacementInvoiceAutoFillPriceField.PRODUCTION_RETAIL_PRICE
      );
      this.boardPartToProductionPrice.set(part.id, productionPrice);
    }
    if (this.boardPartToConstructionPrice.get(part.id) == null) {
      let constructionPrice = new PriceLine();
      constructionPrice.product = this.inputBoard;
      constructionPrice.boardPart = part;
      constructionPrice.text = 'Plaatsingsprijs constructie';
      constructionPrice.override = OVERRIDES.CONSTRUCTION_PRICE.value;
      this.boardPartToConstructionPrice.set(part.id, constructionPrice);
    }
    if (this.boardPartToPlacementPrice.get(part.id) == null) {
      let placementPrice = new PriceLine();
      placementPrice.product = this.inputBoard;
      placementPrice.boardPart = part;
      placementPrice.text = 'Plaatsingsprijs installateur';
      placementPrice.override = OVERRIDES.PLACEMENT_PRICE.value;
      this.fillBoardPartPrice(
        placementPrice,
        part,
        PlacementInvoiceAutoFillPriceField.PLACEMENT_PRICE,
        PlacementInvoiceAutoFillPriceField.PLACEMENT_RETAIL_PRICE
      );
      this.boardPartToPlacementPrice.set(part.id, placementPrice);
    }
    if (this.boardPartToDistanceToBottomPrice.get(part.id) == null) {
      let distanceToBottomPrice = new PriceLine();
      distanceToBottomPrice.product = this.inputBoard;
      distanceToBottomPrice.boardPart = part;
      distanceToBottomPrice.text = 'Verhoging bord';
      distanceToBottomPrice.override = OVERRIDES.DISTANCE_TO_BOTTOM_PRICE.value;
      this.boardPartToDistanceToBottomPrice.set(part.id, distanceToBottomPrice);
    }
    if (this.boardPartToTransportPrice.get(part.id) == null) {
      let transportPrice = new PriceLine();
      transportPrice.product = this.inputBoard;
      transportPrice.boardPart = part;
      transportPrice.text = 'Transportprijs';
      transportPrice.override = OVERRIDES.TRANSPORT_PRICE.value;

      // Auto-fill transport price only for the first eligible board part across the entire project
      if (part.signSize != null) {
        const firstEligibleId =
          this.findGlobalFirstTransportEligibleBoardPartId();
        if (
          firstEligibleId != null &&
          part.id === firstEligibleId &&
          this.shouldIncludeTransportPrice(part)
        ) {
          this.doFillTransportPrice(transportPrice, part);
        }
      }

      this.boardPartToTransportPrice.set(part.id, transportPrice);
    }
    if (this.boardPartToPaintedPostsPrice.get(part.id) == null) {
      let paintedPostsPrice = new PriceLine();
      paintedPostsPrice.product = this.inputBoard;
      paintedPostsPrice.boardPart = part;
      paintedPostsPrice.text = 'Geschilderde constructieprijs';
      paintedPostsPrice.override = OVERRIDES.PAINTED_POSTS_PRICE.value;
      this.fillBoardPartPrice(
        paintedPostsPrice,
        part,
        PlacementInvoiceAutoFillPriceField.PAINTED_POSTS_PRICE
      );
      this.boardPartToPaintedPostsPrice.set(part.id, paintedPostsPrice);
    }
    if (this.boardPartToRepoPrice.get(part.id) == null) {
      let repoPrice = new PriceLine();
      repoPrice.product = this.inputBoard;
      repoPrice.boardPart = part;
      repoPrice.text = 'Weghaalprijs';
      repoPrice.override = OVERRIDES.REPO_PRICE.value;
      this.fillBoardPartPrice(
        repoPrice,
        part,
        PlacementInvoiceAutoFillPriceField.REMOVAL_PRICE
      );
      this.boardPartToRepoPrice.set(part.id, repoPrice);
    }
  }

  doUpdate() {
    this.loadingService.isLoading();
    this.savePrices.next();
  }

  combinePrices() {
    let outputList = [...this.otherPrices];

    if (CollectionHelper.isPopulated(this.inputBoard.boardParts)) {
      this.inputBoard.boardParts.forEach((part) => {
        if (part.id == null) {
          return;
        }
        outputList.push(this.boardPartToProductionPrice.get(part.id));
        outputList.push(this.boardPartToConstructionPrice.get(part.id));
        outputList.push(this.boardPartToPlacementPrice.get(part.id));
        if (part.distanceToBottom == null) {
          outputList.push(this.boardPartToDistanceToBottomPrice.get(part.id));
        }
        outputList.push(this.boardPartToTransportPrice.get(part.id));
        if (part.paintedPosts) {
          outputList.push(this.boardPartToPaintedPostsPrice.get(part.id));
        }
        outputList.push(this.boardPartToRepoPrice.get(part.id));
      });
    }

    this.projectDisplayObservableService
      .getProjectInvoicePriceChangedSubscribable()
      .next();
    this.output.emit(outputList);
  }

  fillPrices(event: ProductSizeChangedEvent): void {
    const boardPart = !CollectionHelper.isPopulated(this.inputBoard.boardParts)
      ? null
      : this.inputBoard.boardParts.find((bp) => bp.id === event.partId);

    if (boardPart == null) {
      return;
    }

    boardPart.signSize == null
      ? this.resetBoardPartPrices(boardPart)
      : this.fillBoardPartPricesFromSignSize(boardPart);
    this.combinePrices();
  }

  private resetBoardPartPrices(part: BoardPart): void {
    this.boardPartToProductionPrice.get(part.id).price = '0';
    this.boardPartToPlacementPrice.get(part.id).price = '0';
    this.boardPartToPaintedPostsPrice.get(part.id).price = '0';
    this.boardPartToRepoPrice.get(part.id).price = '0';
    this.boardPartToTransportPrice.get(part.id).price = '0';
  }

  private fillBoardPartPricesFromSignSize(part: BoardPart): void {
    const productionPrice =
      ProductHelper.getBoardPartProductionRetailPriceIfApplicable(
        part,
        this.isRetailer
      );
    const placementPrice =
      ProductHelper.getBoardPartPlacementRetailPriceIfApplicable(
        part,
        this.isRetailer
      );
    this.boardPartToProductionPrice.get(part.id).price = `${
      Number(productionPrice == null ? 0 : productionPrice) * this.companyFactor
    }`;
    this.boardPartToPlacementPrice.get(part.id).price = `${
      Number(placementPrice == null ? 0 : placementPrice) * this.companyFactor
    }`;
    this.boardPartToPaintedPostsPrice.get(part.id).price = `${
      Number(
        part.signSize.paintedPostsPrice == null
          ? 0
          : part.signSize.paintedPostsPrice
      ) * this.companyFactor
    }`;
    this.boardPartToRepoPrice.get(part.id).price = `${
      Number(
        part.signSize.removalPrice == null ? 0 : part.signSize.removalPrice
      ) * this.companyFactor
    }`;

    // Auto-fill transport price only for the first eligible board part across the entire project
    const firstEligibleId = this.findGlobalFirstTransportEligibleBoardPartId();
    if (
      firstEligibleId != null &&
      part.id === firstEligibleId &&
      this.shouldIncludeTransportPrice(part)
    ) {
      this.doFillTransportPrice(
        this.boardPartToTransportPrice.get(part.id),
        part
      );
    } else {
      this.boardPartToTransportPrice.get(part.id).price = '0';
    }
  }

  /**
   * Determines whether transport price should be automatically included based on board format and retailer status.
   * Rules:
   * - Only for NON-retailers (companies where 'Is retailer' is unchecked)
   * - Only for boards larger than 200x200 (not equal to 200x200)
   * - Placement types: plaatsing & weghalen
   */
  private shouldIncludeTransportPrice(part: BoardPart): boolean {
    if (part.height == null || part.width == null) {
      return false;
    }

    // Only for NON-retailers
    if (this.isRetailer) {
      return false;
    }

    // Only for BaseProject or Repo subprojects
    const subType = this.subProject?.type;
    const isBaseOrRepo =
      subType === SUB_PROJECT_TYPES.BASE_PROJECT ||
      subType === SUB_PROJECT_TYPES.REPO;
    if (!isBaseOrRepo) {
      return false;
    }

    // Only for boards larger than 200x200 (not equal to 200x200)
    if (this.isLargerThan2x2Format(part)) {
      return true;
    }

    return false;
  }

  /**
   * Checks if the board part is larger than 2x2 format (200cm x 200cm)
   */
  private isLargerThan2x2Format(part: BoardPart): boolean {
    // Consider it larger if either dimension is greater than 200cm
    // or if the total area is greater than 2x2 (40,000 cm²)
    const area = part.height * part.width;
    return area > 40000 || part.height > 200 || part.width > 200;
  }

  /**
   * Fills transport price using distance-based calculation
   */
  private doFillTransportPrice(priceLine: PriceLine, part: BoardPart): void {
    const kilometersTo = this.subProject?.projectPlacement?.kilometersTo;
    const transportRate = part.signSize?.transportPrice;

    if (kilometersTo == null || transportRate == null) {
      priceLine.price = '0';
      return;
    }

    // Calculate transport price: Math.ceil(distance × rate)
    const calculatedPrice = Number(transportRate) * kilometersTo;
    const roundedUpPrice = Math.ceil(calculatedPrice);

    priceLine.price = `${roundedUpPrice * this.companyFactor}`;
  }

  /**
   * Finds the first eligible board part across the entire subproject
   */
  private findGlobalFirstTransportEligibleBoardPartId(): number | null {
    const products: any[] = (this.subProject as any)?.project?.products;
    if (!CollectionHelper.isPopulated(products)) {
      return null;
    }
    for (const product of products) {
      if (ProductHelper.isBoard(product)) {
        const board = product as unknown as Board;
        if (CollectionHelper.isPopulated(board.boardParts)) {
          for (const part of board.boardParts) {
            if (
              part &&
              part.id != null &&
              this.shouldIncludeTransportPrice(part)
            ) {
              return part.id;
            }
          }
        }
      }
    }
    return null;
  }

  private fillBoardPartPrice(
    priceLine: PriceLine,
    part: BoardPart,
    autoFillField: PlacementInvoiceAutoFillPriceField,
    retailAutoFillField?: PlacementInvoiceAutoFillPriceField
  ): void {
    if (part.signSize != null && !this.subProject.quotationSent) {
      const retailFillPrice = this.getBoardPartRetailPriceIfApplicable(
        part,
        retailAutoFillField
      );
      if (retailFillPrice != null) {
        this.doFillBoardPartPrice(priceLine, retailFillPrice);
      } else {
        this.doFillBoardPartPrice(priceLine, part.signSize[autoFillField]);
      }
    }
  }

  private doFillBoardPartPrice(priceLine: PriceLine, value: string): void {
    priceLine.price = `${
      Number(value == null ? 0 : value) * this.companyFactor
    }`;
  }

  private getBoardPartRetailPriceIfApplicable(
    part: BoardPart,
    retailAutoFillField: PlacementInvoiceAutoFillPriceField
  ): string {
    return !this.isRetailer || retailAutoFillField == null
      ? null
      : part.signSize[retailAutoFillField];
  }
}
